<template>
  <div class="input-wrapper">
    <label v-if="label" :for="`${name}`">
      {{ label }}
      <template v-if="showRequired">
        <template v-if="required">*</template>
        <template v-else>(optional)</template>
      </template>
    </label>
    <div
      class="input-row"
      :class="[
        className,
        {
          'input-row--error': error,
          'input-row--icon--left': hasIconLeft,
          'input-row--icon--right': hasIconRight,
          'input-row--disabled': disabled,
        },
      ]"
      v-bind="$attrs"
    >
      <slot name="icon"></slot>
      <div v-if="prefixText" class="input-row--prefix-text">{{ prefixText }}</div>
      <slot name="input"></slot>
      <div v-if="suffixText" class="input-row--suffix-text">{{ suffixText }}</div>
      <slot name="iconRight"></slot>
    </div>
    <div v-if="helperText || error || (characterLimit && model.length > 0)" class="input-row--bottom-text">
      <div v-if="!error" class="input-row--helper-text">
        {{ helperText }}
      </div>

      <ErrorText v-if="error" :error="error" />

      <div
        v-if="characterLimit && model && model.length > 0"
        class="input-row--character-limit"
        :class="{'input-row--character-limit__error': model.length > characterLimit}"
      >
        {{ model.length }} / {{ characterLimit }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, useSlots } from 'vue';
import ErrorText from './ErrorText.vue';

const slots = useSlots()

const model = defineModel<any>();

const hasIconLeft = computed(() => !!slots.icon);
const hasIconRight = computed(() => !!slots.iconRight);

withDefaults(
  defineProps<{
    label?: string;

    helperText?: string;

    disabled?: boolean;
    required?: boolean;
    showRequired?: boolean;

    className?: string;
    error?: string;

    name?: string;

    characterLimit?: number;
    prefixText?: string;
    suffixText?: string;
  }>(),
  {
    className: '',
    label: '',
    disabled: false,
    required: false,
    showRequired: true,
    name: '',
    error: '',
  },
);

</script>

<style lang="scss">
.input-wrapper {
  display: block;
}


.input-row {
  @include mixins.rounded-border();
  background: variables.$primitives-color-background-100;
  box-shadow:
    0 0 #0000,
    0 0 #0000,
    0 1px 2px 0 rgba(18, 18, 23, 0.05);
  color: variables.$primitives-color-tertiary-500;
  position: relative;
  transition:
    border-color 0.2s ease-in-out,
    color 0.2s ease-in-out;

  &.input-row--icon--right,
  &.input-row--icon--left {
    @include mixins.flex-center($horiz: flex-start, $gap: variables.$sizing-xxxs);

    .form-input {
      @include sizing.padding('xxs');
      width: 100%;
    }
  }

  &.input-row--hover,
  &:hover {
    border-color: variables.$primitives-color-tertiary-300;
  }

  &.input-row--active,
  &:has(.form-input:focus) {
    border-color: variables.$primitives-color-primary-500;
  }

  &.input-row--error {
    border-color: variables.$primitives-color-red-300;
  }

  .form-input {
    @include sizing.padding('xxs', 'xs');
    @include typography.font-body($weight: normal, $size: md);
    border-radius: variables.$sizing-xxs;
    flex-grow: 1;
    width: 100%;

    &:disabled {
      background: variables.$primitives-color-border-100;
      color: variables.$primitives-color-primary-400;
    }
  }

  svg {
    color: gray;
    width: 20px;
  }

  &.input-row--icon--left {
    > svg {
      margin-left: 8px;
    }
  }

  &.input-row--icon--right {
    svg {
      margin-right: 8px;
    }
  }
}

.input-row.input-row--disabled {
  background-color: variables.$primitives-color-background-200;
  border-color: transparent;

  &:hover {
    border-color: transparent;
  }

  &.input-row--select {
    .form-input--select {
      cursor: default;
    }
  }
}

.input-row--prefix-text {
  padding-left: variables.$sizing-sm;
}

.input-row--suffix-text {
  padding-right: variables.$sizing-sm;
}

.input-row--bottom-text {
  @include mixins.flex-center($horiz: flex-start);
  @include typography.font-caption();
  color: variables.$primitives-color-tertiary-400;
  margin-top: variables.$sizing-xxs;

  .input-row--helper-text {
    margin-right: auto;
  }

  .input-row--character-limit {
    margin-left: auto;
  }

  .input-row--character-limit__error {
    color: variables.$primitives-color-red-300;
  }
}

.input-row.input-row--icon--left .form-input--clear-button svg {
  margin: 0;
}

</style>
